export interface TenantIdentity {
  customer_id: string;
  customer_name: string;
}

/** Keep tenant labels recognizable while disambiguating duplicate names. */
export function getTenantDisplayNames(tenants: readonly TenantIdentity[]): Map<string, string> {
  const normalizeLabel = (label: string) => label.trim().toLowerCase();
  const normalizedNames = tenants.map(tenant => normalizeLabel(tenant.customer_name));
  const nameCounts = new Map<string, number>();

  normalizedNames.forEach(name => {
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  });

  const labels = tenants.map((tenant, index) =>
    (nameCounts.get(normalizedNames[index]) ?? 0) > 1
      ? `${tenant.customer_name} (${tenant.customer_id})`
      : tenant.customer_name
  );

  while (true) {
    const labelCounts = new Map<string, number>();
    labels.forEach(label => {
      const normalizedLabel = normalizeLabel(label);
      labelCounts.set(normalizedLabel, (labelCounts.get(normalizedLabel) ?? 0) + 1);
    });

    const duplicateLabels = new Set(
      Array.from(labelCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([label]) => label)
    );
    if (duplicateLabels.size === 0) break;

    labels.forEach((label, index) => {
      if (duplicateLabels.has(normalizeLabel(label))) {
        labels[index] = `${label.trim()} [tenant ${tenants[index].customer_id} #${index + 1}]`;
      }
    });
  }

  return new Map(tenants.map((tenant, index) => [tenant.customer_id, labels[index]]));
}
